import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { brandCss } from '../utils/brandColors';

/**
 * Бренд склада (облако, решение владельца 28.09): название, логотип, цвета,
 * заголовок входа — по адресу, с которого открыт сайт (GET /api/platform/brand,
 * без входа).
 *
 * Full-Fix (стек без облака): сервер отвечает default: true — ничего не меняем,
 * всё как было (логотип FULL-FIX, оранжевые токены из tokens.css). Сервер не
 * ответил — тоже остаёмся на Full-Fix: вход не должен зависеть от бренда.
 *
 * У склада облака: CSS-переменные акцента для обеих тем (utils/brandColors) —
 * <style id="brand-tokens"> после tokens.css, заголовок вкладки, значок.
 */
export const FULLFIX_BRAND = {
  default: true,
  product_name: 'Full-Fix',
  short_name: 'FF',
  logo_url: null,
  favicon_url: '/favicon.svg',
  primary: '#E8520E',
  accent: null,
  login_title: 'Управляйте складом без хаоса',
  login_subtitle: 'Система управления складом',
  support_contact: '',
  site: 'full-fix.ru',
  powered_by: false,
  powered_by_text: 'Работает на Full-Fix WMS',
  powered_by_url: 'https://full-fix.ru',
};

const CACHE_KEY = 'wms_brand';

// Адрес склада облака: <slug>.wms.full-fix.ru (сам full-fix — не поддомен wms)
function isCloudHost() {
  try { return /\.wms\.full-fix\.ru$/i.test(window.location.hostname); } catch { return false; }
}
// Пока бренд склада не пришёл — нейтрально: без оранжевого Full-Fix (обход 29.09)
const CLOUD_PENDING = {
  ...FULLFIX_BRAND, default: false, product_name: 'Склад', short_name: 'С', primary: '#475569',
  login_title: 'Вход в систему склада', login_subtitle: 'Система управления складом', site: '', powered_by: false,
};
const BrandContext = createContext({ brand: FULLFIX_BRAND, reload: () => {} });

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function applyBrand(b) {
  const doc = document;
  let style = doc.getElementById('brand-tokens');
  if (!b || b.default) {
    if (style) style.remove();
    return;
  }
  const css = brandCss(b.primary, b.accent);
  if (css) {
    if (!style) {
      style = doc.createElement('style');
      style.id = 'brand-tokens';
      doc.head.appendChild(style);
    }
    style.textContent = css;
  }
  if (b.product_name) doc.title = b.product_name;
  if (b.favicon_url) {
    doc.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => {
      l.setAttribute('href', b.favicon_url);
      if (!/\.svg(\?|$)/.test(b.favicon_url)) l.removeAttribute('type');
    });
  }
}

export function BrandProvider({ children }) {
  // Кэш — чтобы у склада облака не мелькал Full-Fix до ответа сервера
  const [brand, setBrand] = useState(() => {
    const cached = readCache();
    if (cached && cached.product_name) return cached;
    // Склад облака (<slug>.wms.full-fix.ru) при первом заходе без ответа сервера
    // показывал вход «FULL-FIX … менеджер Full-Fix» (проход 28.09, сеть моргнула) —
    // до ответа держим нейтральное оформление без чужого имени
    return isCloudHost() ? CLOUD_PENDING : FULLFIX_BRAND;
  });

  const reload = useCallback(async (attempt = 0) => {
    try {
      const r = await fetch('/api/platform/brand', { credentials: 'same-origin' });
      if (!r.ok) { if (attempt < 2 && r.status >= 500) setTimeout(() => reload(attempt + 1), 2000); return; }
      const b = await r.json();
      if (!b || typeof b !== 'object' || !b.product_name) return;
      setBrand(b.default ? FULLFIX_BRAND : { ...FULLFIX_BRAND, ...b });
      try {
        if (b.default) localStorage.removeItem(CACHE_KEY);
        else localStorage.setItem(CACHE_KEY, JSON.stringify(b));
      } catch { /* приватный режим */ }
    } catch {
      // сеть: остаёмся на том, что есть, и пробуем ещё два раза
      if (attempt < 2) setTimeout(() => reload(attempt + 1), 2000);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { applyBrand(brand); }, [brand]);

  return <BrandContext.Provider value={{ brand, reload }}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext);
}

/**
 * Для текстов в больших страницах: склад облака или Full-Fix. Бренд склада облака
 * кладётся в localStorage при старте (до входа), к открытию рабочих экранов он уже
 * известен. У Full-Fix кэша нет → прежний текст.
 */
function cloudBrand() {
  const b = readCache();
  if (b && !b.default && b.product_name) return b;
  // Кэша ещё нет (новое устройство, приватный режим), а адрес — склада облака:
  // тексты облака, а не Full-Fix («Объединить с другой приёмкой», а не «Слить с плановой»)
  return !b && isCloudHost() ? CLOUD_PENDING : null;
}

/** Склад облака (не full-fix): бренд склада, а не встроенный */
export function isCloudBrand() {
  return !!cloudBrand();
}

/** Название продукта в тексте: «Full-Fix» или название склада облака */
export function brandName() {
  const b = cloudBrand();
  return b ? b.product_name : 'Full-Fix';
}

/** Текст full-fix как был — или нейтральный для склада облака */
export function forBrand(fullfixText, cloudText) {
  return cloudBrand() ? cloudText : fullfixText;
}

/**
 * Склад облака без клиентов — «Склад селлера» или модуль «Клиенты» выключен в каталоге:
 * товар свой, компания одна (магазин). Модули — из кэша тарифа (AuthContext, 'wms_plan');
 * у Full-Fix и у фулфилмента облака — false, тексты прежние.
 */
export function isSellerCloud() {
  if (!cloudBrand()) return false;
  try {
    const plan = JSON.parse(localStorage.getItem('wms_plan') || 'null');
    return !!(plan && plan.modules && plan.modules.clients === false);
  } catch {
    return false;
  }
}

/** Текст как был — или для селлера (свой магазин, без клиентов, биллинга и ТЗ клиента) */
export function forSeller(defaultText, sellerText) {
  return isSellerCloud() ? sellerText : defaultText;
}
